import { useEffect, useRef, useState } from 'react'
import {
  listProblemReportNotifications,
  getOwnProblemReportNotificationTarget,
} from '../../api/generated/problem-reports/problem-reports'
import { getAdminProblemReportNotificationTarget } from '../../api/generated/admin-problem-reports/admin-problem-reports'
import { Button } from '../../components/ui/button'
import { useT } from '../../i18n'
import { formatDateTime } from '../../lib/datetime'
import { reportCopy } from './copy'
import { reportRead, useReportRead } from './own-data'
export default function ReportNotifications({
  onOwn,
  onAdmin,
}: {
  onOwn: (id: string) => void
  onAdmin?: (id: string) => void
}) {
  const t = useT(reportCopy)
  const [cursor, setCursor] = useState<string | undefined>()
  const [error, setError] = useState(false)
  const [pending, setPending] = useState(false)
  const request = useRef<AbortController | null>(null)
  useEffect(
    () => () => {
      request.current?.abort()
    },
    [],
  )
  const query = useReportRead(['notifications', cursor], (options) =>
    listProblemReportNotifications({ cursor, limit: 10 }, options),
  )
  async function select(id: string, admin: boolean) {
    request.current?.abort()
    const controller = new AbortController()
    request.current = controller
    setPending(true)
    setError(false)
    try {
      const result = await reportRead(controller.signal, (options) =>
        admin
          ? getAdminProblemReportNotificationTarget(id, options)
          : getOwnProblemReportNotificationTarget(id, options),
      )
      if (result.status !== 200) throw new Error()
      if (result.data.audience === 'admin' && admin)
        onAdmin?.(result.data.report_id)
      else if (result.data.audience === 'own' && !admin)
        onOwn(result.data.report_id)
      else setError(true)
    } catch {
      if (!controller.signal.aborted) setError(true)
    } finally {
      if (!controller.signal.aborted) setPending(false)
    }
  }
  if (query.isPending) return <p role="status">{t.loading}</p>
  if (query.isError || query.data?.status !== 200)
    return <p role="alert">{t.error}</p>
  const page = query.data.data
  return (
    <section className="space-y-3">
      <h3 className="font-medium">{t.notifications}</h3>
      {error && <p role="alert">{t.unavailableTarget}</p>}
      {!page.items.length && <p className="text-sm">{t.noUpdates}</p>}
      {page.items.map((item) => (
        <div
          key={item.id}
          className="space-y-2 rounded-[var(--radius-control)] border border-[var(--color-border)] p-3 text-sm"
        >
          <p className="font-medium">{item.title}</p>
          <p>{item.body}</p>
          <time className="text-xs text-[var(--color-text-muted)]">
            {formatDateTime(item.created_at)}
          </time>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={pending}
              onClick={() => void select(item.id, false)}
            >
              {t.openOwn}
            </Button>
            {onAdmin && (
              <Button
                size="sm"
                variant="outline"
                disabled={pending}
                onClick={() => void select(item.id, true)}
              >
                {t.openAdmin}
              </Button>
            )}
          </div>
        </div>
      ))}
      <div className="flex gap-2">
        {cursor && (
          <Button variant="ghost" onClick={() => setCursor(undefined)}>
            {t.back}
          </Button>
        )}
        {page.has_more && page.next_cursor && (
          <Button
            variant="outline"
            onClick={() => setCursor(page.next_cursor!)}
          >
            {t.next}
          </Button>
        )}
      </div>
    </section>
  )
}
